/**
 * The mode of a multi-value filter. It travels as an integer, so the values are
 * explicit and must match the backend's DynamicFilterType. Not every control
 * offers IsAllOf: a MultiSelect shows it only when opted in through
 * `advancedFilterTypes` (the Filters organisms' `allowAllOf`), and endpoints
 * without an All-of implementation reject 2 with a 400.
 */
export enum ApiDynamicFilterType {
  IsAnyOf = 0,
  IsNoneOf = 1,
  IsAllOf = 2,
}

export type ApiDynamicFilter<T extends string | number> = {
  filterType?: ApiDynamicFilterType
  values?: T[] | null
}

export type QuestionFilter = {
  question?: string | null
  answers?: string[] | null
}

export type QuestionDynamicFilter = {
  question?: string | null
  answers?: string[] | null
  filterType?: ApiDynamicFilterType
}

/**
 * ID-based question filter entry (mirrors the generated
 * FormQuestionOptionsDynamicFilter schema): matches by question id + the
 * selected options' ORDER values (what answer rows store), not option ids or
 * display text. Entries with an empty optionOrders selection are ignored by
 * the API. Used by ApplicationFilter's applicationQuestionFilter (application
 * form) and projectQuestionFilter (project form) arrays — two separate fields
 * because the question ids live in different tables and can collide.
 */
export type QuestionOptionsDynamicFilter = {
  questionId?: number
  filterType?: ApiDynamicFilterType
  optionOrders?: number[] | null
}

/**
 * This is an impossible type to represent in TypeScript, since TypeScript does not support negation
 * of types. So "filters" is actually of type ApiDynamicFilter | string | number | null | undefined
 * We're just keeping the property name for documentation purposes. Unfortunately it's impossible to
 * create a general type that can be applied to all filters. An alternative solution could be to make
 * this type a union of all the possible filters so we can use it for the Filters component and correctly
 * type match the search params
 */
export type ApiFilters = {
  orderBy?: number
  skip?: number
  take?: number
  filters?: Array<QuestionFilter | QuestionDynamicFilter> | null
} & {
  [key: string]:
    | ApiDynamicFilter<string>
    | ApiDynamicFilter<number>
    | Array<QuestionFilter | QuestionDynamicFilter>
    | QuestionOptionsDynamicFilter[]
    | string
    | number
    | null
    | undefined
}
